import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './PartialSummary.css';

// Define types for the summary result
interface SummaryResult {
  report_title?: string;
  summary?: string;
  key_findings?: string[];
  recommendations?: string[];
  updated_notes?: string[];
  partial_text?: string;
  reports?: any[];
}

// Component to display partial summary results with typing animation
const PartialSummaryDisplay: React.FC<{ result: SummaryResult }> = ({ result }) => {
  const { t } = useTranslation('create');
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Keep track of what has been rendered
  const renderedRef = useRef<{
    title: string | null;
    summary: string | null;
    keyFindings: string[] | null;
    recommendations: string[] | null;
  }>({
    title: null,
    summary: null,
    keyFindings: null,
    recommendations: null
  });
  
  // Try to parse JSON from partial_text if it exists
  let parsedPartialText: any = null;
  if (result.partial_text) {
    try {
      // First try to parse as complete JSON
      if (result.partial_text.trim().startsWith('{')) {
        try {
          parsedPartialText = JSON.parse(result.partial_text);
          console.log('Successfully parsed JSON from summary partial_text:', Object.keys(parsedPartialText));
        } catch (error) {
          // If complete JSON parsing fails, try to extract partial data
          console.log('Failed to parse complete JSON, trying to extract partial data');
          try {
            // Try to extract structured data from the partial text
            // Look for report_title - handle escaped quotes and incomplete strings
            const titleRegex = /"report_title"\s*:\s*"((?:\\"|[^"])*)(?:"|$)/;
            const titleMatch = result.partial_text.match(titleRegex);
            if (titleMatch && titleMatch[1]) {
              if (!parsedPartialText) parsedPartialText = {};
              // Handle case where string ends with backslash (incomplete escape sequence)
              let titleText = titleMatch[1];
              if (titleText.endsWith('\\') && !titleText.endsWith('\\\\')) {
                // Remove trailing backslash if it's not a double backslash
                titleText = titleText.slice(0, -1);
              }
              parsedPartialText.report_title = titleText.replace(/\\"/g, '"').replace(/\\n/g, '\n');
            }
            
            // Look for summary - handle escaped quotes
            const summaryRegex = /"summary"\s*:\s*"((?:\\"|[^"])*)(?:"|$)/;
            const summaryMatch = result.partial_text.match(summaryRegex);
            if (summaryMatch && summaryMatch[1]) {
              if (!parsedPartialText) parsedPartialText = {};
              // Handle case where string ends with backslash (incomplete escape sequence)
              let summaryText = summaryMatch[1];
              if (summaryText.endsWith('\\') && !summaryText.endsWith('\\\\')) {
                // Remove trailing backslash if it's not a double backslash
                summaryText = summaryText.slice(0, -1);
              }
              parsedPartialText.summary = summaryText.replace(/\\"/g, '"').replace(/\\n/g, '\n');
            }
            
            // Look for key_findings array - using [\s\S] instead of /s flag for ES2018 compatibility
            // Also handle incomplete arrays
            const keyFindingsMatch = result.partial_text.match(/"key_findings"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
            if (keyFindingsMatch && keyFindingsMatch[1]) {
              if (!parsedPartialText) parsedPartialText = {};
              // Extract individual findings - handle escaped quotes and incomplete strings
              const findingsRegex = /"((?:\\"|[^"])*)(?:"|$)/g;
              const findings = [];
              let findingMatch;
              let findingText = keyFindingsMatch[1];
              
              // Process the array content
              while ((findingMatch = findingsRegex.exec(findingText)) !== null) {
                if (findingMatch[1]) {
                  let text = findingMatch[1];
                  // Handle case where string ends with backslash (incomplete escape sequence)
                  if (text.endsWith('\\') && !text.endsWith('\\\\')) {
                    text = text.slice(0, -1);
                  }
                  // Improved handling of escaped characters
                  findings.push(
                    text
                      .replace(/\\"/g, '"')  // Replace \" with "
                      .replace(/\\n/g, '\n') // Replace \n with \n
                      .replace(/\\\\/g, '\\') // Replace \\ with \
                  );
                }
              }
              
              if (findings.length > 0) {
                parsedPartialText.key_findings = findings;
              }
            }
            
            // Look for recommendations array - using [\s\S] instead of /s flag for ES2018 compatibility
            // Also handle incomplete arrays
            const recommendationsMatch = result.partial_text.match(/"recommendations"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
            if (recommendationsMatch && recommendationsMatch[1]) {
              if (!parsedPartialText) parsedPartialText = {};
              // Extract individual recommendations - handle escaped quotes and incomplete strings
              const recommendationsRegex = /"((?:\\"|[^"])*)(?:"|$)/g;
              const recommendations = [];
              let recommendationMatch;
              let recommendationText = recommendationsMatch[1];
              
              // Process the array content
              while ((recommendationMatch = recommendationsRegex.exec(recommendationText)) !== null) {
                if (recommendationMatch[1]) {
                  let text = recommendationMatch[1];
                  // Handle case where string ends with backslash (incomplete escape sequence)
                  if (text.endsWith('\\') && !text.endsWith('\\\\')) {
                    text = text.slice(0, -1);
                  }
                  // Improved handling of escaped characters
                  recommendations.push(
                    text
                      .replace(/\\"/g, '"')  // Replace \" with "
                      .replace(/\\n/g, '\n') // Replace \n with \n
                      .replace(/\\\\/g, '\\') // Replace \\ with \
                  );
                }
              }
              
              if (recommendations.length > 0) {
                parsedPartialText.recommendations = recommendations;
              }
            }
            
            if (parsedPartialText) {
              console.log('Successfully extracted partial data from summary:', 
                parsedPartialText.report_title ? 'report_title found' : '', 
                parsedPartialText.summary ? 'summary found' : '',
                parsedPartialText.key_findings ? `${parsedPartialText.key_findings.length} key_findings found` : '',
                parsedPartialText.recommendations ? `${parsedPartialText.recommendations.length} recommendations found` : '');
            } else {
              console.log('No usable partial data found in summary');
            }
          } catch (extractError) {
            console.log('Error extracting partial data from summary:', extractError);
            parsedPartialText = null;
          }
        }
      }
    } catch (error) {
      console.log('Failed to process summary partial_text:', error);
      parsedPartialText = null;
    }
  }

  // Use effect to render the content
  useEffect(() => {
    if (contentRef.current) {
      // Determine what content to render
      const title = result.report_title || 
                   (parsedPartialText && parsedPartialText.report_title);
      
      const summary = result.summary || 
                     (parsedPartialText && parsedPartialText.summary);
      
      const keyFindings = result.key_findings || 
                         (parsedPartialText && parsedPartialText.key_findings) || 
                         [];
      
      const recommendations = result.recommendations || 
                             (parsedPartialText && parsedPartialText.recommendations) || 
                             [];
      
      // Update the content if it has changed
      if (title !== renderedRef.current.title) {
        const titleElement = contentRef.current.querySelector('.summary-title') as HTMLElement;
        if (titleElement) {
          titleElement.textContent = title || t('results.final_report');
          renderedRef.current.title = title;
        }
      }
      
      if (summary !== renderedRef.current.summary) {
        const summaryElement = contentRef.current.querySelector('.summary-content p') as HTMLElement;
        if (summaryElement) {
          summaryElement.textContent = summary || '';
          renderedRef.current.summary = summary;
        }
      }
      
      if (JSON.stringify(keyFindings) !== JSON.stringify(renderedRef.current.keyFindings)) {
        const keyFindingsList = contentRef.current.querySelector('.key-findings ul') as HTMLElement;
        if (keyFindingsList) {
          keyFindingsList.innerHTML = '';
          keyFindings.forEach((finding: string, index: number) => {
            const li = document.createElement('li');
            li.textContent = finding;
            keyFindingsList.appendChild(li);
          });
          renderedRef.current.keyFindings = keyFindings;
        }
      }
      
      if (JSON.stringify(recommendations) !== JSON.stringify(renderedRef.current.recommendations)) {
        const recommendationsList = contentRef.current.querySelector('.recommendations ul') as HTMLElement;
        if (recommendationsList) {
          recommendationsList.innerHTML = '';
          recommendations.forEach((recommendation: string, index: number) => {
            const li = document.createElement('li');
            li.textContent = recommendation;
            recommendationsList.appendChild(li);
          });
          renderedRef.current.recommendations = recommendations;
        }
      }
      
      // Show/hide sections based on content availability
      const summarySection = contentRef.current.querySelector('.summary-content') as HTMLElement;
      if (summarySection) {
        summarySection.style.display = summary ? 'block' : 'none';
      }
      
      const keyFindingsSection = contentRef.current.querySelector('.key-findings') as HTMLElement;
      if (keyFindingsSection) {
        keyFindingsSection.style.display = keyFindings.length > 0 ? 'block' : 'none';
      }
      
      const recommendationsSection = contentRef.current.querySelector('.recommendations') as HTMLElement;
      if (recommendationsSection) {
        recommendationsSection.style.display = recommendations.length > 0 ? 'block' : 'none';
      }
      
      const partialTextSection = contentRef.current.querySelector('.partial-text') as HTMLElement;
      if (partialTextSection) {
        // Show partial_text only if no structured data is available
        const hasStructuredData = summary || keyFindings.length > 0 || recommendations.length > 0;
        partialTextSection.style.display = !hasStructuredData && result.partial_text ? 'block' : 'none';
        
        if (!hasStructuredData && result.partial_text) {
          const preElement = partialTextSection.querySelector('pre') as HTMLElement;
          if (preElement) {
            preElement.textContent = result.partial_text;
          }
        }
      }
    }
  }, [result, parsedPartialText]);

  return (
    <div className="partial-summary">
      <h2>{t('processing.summary_generating_title')}</h2>

      <div className="partial-content" ref={contentRef}>
        <h3 className="summary-title">{t('results.final_report')}</h3>

        <div className="summary-content">
          <h4>{t('results.summary_forming')}</h4>
          <p></p>
        </div>

        <div className="key-findings">
          <h4>{t('results.key_findings_forming')}</h4>
          <ul></ul>
        </div>

        <div className="recommendations">
          <h4>{t('results.recommendations_forming')}</h4>
          <ul></ul>
        </div>

        <div className="partial-text">
          <h4>{t('results.forming_summary')}</h4>
          <pre></pre>
        </div>
      </div>
      
      <div className="typing-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  );
};

export default PartialSummaryDisplay;
